import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useVerifyPasswordMutation } from '@/store/api/authApi';

interface PasswordExitDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

const PasswordExitDialog: React.FC<PasswordExitDialogProps> = ({
  open,
  onClose,
  onConfirm,
}) => {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [attempts, setAttempts] = useState(0);
  const [verifyPassword, { isLoading }] = useVerifyPasswordMutation();

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(event.target.value);
    if (error) {
      setError('');
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!password || isLoading) {
      return;
    }

    try {
      const result = await verifyPassword({ password }).unwrap();
      
      if (result.data.valid) {
        // Password is correct
        setPassword('');
        setError('');
        setAttempts(0);
        onConfirm();
      } else {
        // Password is incorrect
        setAttempts(prev => prev + 1);
        setError(t('common:fullscreen.incorrect_password', 'Incorrect password. Please try again.'));
        setPassword('');
        
        // Add delay after multiple failed attempts
        if (attempts >= 2) {
          setError(t('common:fullscreen.too_many_attempts', 'Too many failed attempts. Please wait a moment.'));
          setTimeout(() => {
            setError('');
            setAttempts(0);
          }, 3000);
        }
      }
    } catch (err: any) {
      // Handle API errors
      console.error('Password verification failed:', err);
      setAttempts(prev => prev + 1);
      setError(t('common:fullscreen.incorrect_password', 'Incorrect password. Please try again.'));
      setPassword('');
    }
  };

  const handleCancel = () => {
    setPassword('');
    setError('');
    setAttempts(0);
    onClose();
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSubmit(event as React.FormEvent);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        // Prevent closing dialog by clicking outside or pressing escape
        if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
          handleCancel();
        }
      }}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
    >
      <DialogTitle>
        <Typography variant="h6" component="div">
          {t('common:fullscreen.exit_fullscreen', 'Exit Fullscreen')}
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body1" color="text.secondary">
            {t('common:fullscreen.exit_fullscreen_message', 'Please enter the password to exit fullscreen mode.')}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <TextField
            autoFocus
            fullWidth
            type="password"
            label={t('common:fullscreen.password', 'Password')}
            value={password}
            onChange={handlePasswordChange}
            onKeyPress={handleKeyPress}
            variant="outlined"
            disabled={attempts >= 3}
            error={!!error}
            sx={{ mt: 1 }}
          />
        </form>

        {attempts >= 1 && attempts < 3 && (
          <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: 'block' }}>
            {t('common:fullscreen.attempts_remaining', 'Attempts remaining: {{count}}', { count: 3 - attempts })}
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button 
          onClick={handleCancel} 
          variant="outlined"
          disabled={attempts >= 3}
        >
          {t('common:actions.cancel', 'Cancel')}
        </Button>
        <Button 
          onClick={handleSubmit}
          variant="contained"
          disabled={!password || attempts >= 3 || isLoading}
          color="primary"
          startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {isLoading ? 'Verifying...' : t('common:actions.confirm', 'Confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PasswordExitDialog;